import React, {useCallback, useEffect, useMemo, useState} from "react";
import Layout from "../layout/Layout";
import {ClientSelectSingle} from "../../../components/form-v2/searchable-select/ClientSelect";
import {DepartmentsManagementProvider, useDepartmentsManagement} from "./DepartmentsManagementContext";
import {Department} from "../../../api/platform/departments";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import {Action, ActionSelect} from "../../../components/ui/ActionSelect";
import PaginationNoneFloating from "../../../components/ui/Content/PaginationNoneFloating";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {BlueButton} from "../../../components/ui/Buttons";
import Modal from "../../../components/portal/Modal";
import BasicText from "../../../components/form-v2/basic-text";
import {useAuth} from "../../../context/user";
import {useUserManagementBrowsing} from "../../../lib/user-management/UserManagementBrowsingContext";
import {useGetModelStatus} from "@/hooks/useGetModelStatus";
import clsx from "clsx";
import {useCopyID} from "@/lib/misc/useCopyID";
import {
    useArchiveDepartment,
    useDeleteDepartment,
    useUnarchiveDepartment,
    useUnassignUsers,
} from "../../../api/platform/useDepartments";

const DepartmentFilterArchiveStatus: React.FC = () => {

    const ctx = useDepartmentsManagement()
    const auth = useAuth();

    return <div className="bg-white shadow-md p-4 flex flex-row gap-8 items-center">
        <a className={`cursor-pointer ${ctx.archived ? "opacity-50" : ""}`} onClick={() => {
            ctx.setPage(1);
            ctx.setArchived(false)
        }}>Departments ({ctx?.departments?.total})</a>
        <a className={`cursor-pointer ${ctx.archived ? "" : "opacity-50"}`} onClick={() => {
            ctx.setPage(1);
            ctx.setArchived(true)
        }}>Archived</a>
        <div className={"flex-1"}/>
        <div>
            {auth.isSuperAdmin && ctx.client &&
                <a href={"/management/departments/create"}>Add Department</a>}
            {!auth.isSuperAdmin && auth.hasAccessLevelClient && <a href={"/management/departments/create"}>Add Department</a>}
        </div>
    </div>
}

const DepartmentFilterSearch: React.FC = () => {

    const ctx = useDepartmentsManagement()
    const auth = useAuth()

    const browsing = useUserManagementBrowsing()

    return <div className="bg-white shadow-md p-4 flex flex-row gap-8 items-center">
        {auth.isSuperAdmin && <div className="w-64">
            <ClientSelectSingle initialClientID={browsing.clientID} client={ctx.client} onChange={ctx.setClient}/>
        </div>
        }

        <div className="w-64">
            <BasicText value={ctx.search} onChange={ctx.setSearch} placeholder={"Search"}/>
        </div>
    </div>
}


const DepartmentTable: React.FC = () => {

    const ctx = useDepartmentsManagement()

    return <div className="bg-white shadow-md p-4">
        <DepartmentTableHeader/>
        <hr className={"my-4"}/>

        <div className={"flex flex-col gap-4"}>
            {ctx.departments?.items && ctx.departments.items.map((d) => (
                <DepartmentRow department={d} key={d.id}/>
            ))}
            <div className={"flex justify-center"}>
                <PaginationNoneFloating
                    currentPage={ctx.page}
                    itemsPerPage={ctx.limit}
                    dataLength={ctx.departments?.total || 0}
                    togglePageSelect={(page) => ctx.setPage(page)}
                />
            </div>
        </div>
    </div>
}


type DepartmentRowProps = {
    department: Department
}

const DepartmentRow: React.FC<DepartmentRowProps> = (props) => {

    const ctx = useDepartmentsManagement()
    const auth = useAuth()

    const [showArchive, setShowArchive] = useState(false)
    const [showUnassign, setShowUnassign] = useState(false)
    const [showDelete, setShowDelete] = useState(false)

    const navigate = useNavigate()

    const archived = props.department.archived

    const archiveMutation = useArchiveDepartment(props.department.id)
    const unarchiveMutation = useUnarchiveDepartment(props.department.id)
    const unassignMutation = useUnassignUsers(props.department.id)
    const deleteMutation = useDeleteDepartment()

    const handleArchiveToggle = useCallback(async () => {
        try {
            if (archived) {
                await unarchiveMutation.mutateAsync()
            } else {
                await archiveMutation.mutateAsync()
            }
            setShowArchive(false)
        } catch (e: any) {
            ctx.setActionError(e?.message ?? "An error occurred")
        }
    }, [archived, props.department.id])

    const handleUnassignUsers = useCallback(async () => {
        try {
            await unassignMutation.mutateAsync()
            setShowUnassign(false)
        } catch (e: any) {
            ctx.setActionError(e?.message ?? "An error occurred")
        }
    }, [props.department.id])

    const handleDelete = useCallback(async () => {
        try {
            await deleteMutation.mutateAsync(props.department.id)
            setShowDelete(false)
        } catch (e: any) {
            ctx.setActionError(e?.message ?? "An error occurred")
        }
    }, [props.department.id])

    const {
        data: status,
    } = useGetModelStatus("Departments", props.department.id || "")

    const rowClasses = useMemo(() => {
        return clsx(
            "grid grid-cols-[5%_25%_25%_25%_20%]",
            {
                "opacity-50 pointer-events-none": status == "deleting",
            }
        )
    }, [status])

    const {isSuperAdmin} = useAuth()
    const {copyID, isCopied} = useCopyID()

    return <>
        <Modal open={showArchive} setOpen={setShowArchive}>
            {archived &&
                <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
                    <p className={"font-semibold"}>Unarchive Department</p>
                    <p>Please click to confirm unarchiving the department ({props.department.name})</p>
                    <BlueButton text={"Unarchive Department"} loading={unarchiveMutation.isLoading} onClick={handleArchiveToggle}/>
                </div>}

            {!archived &&
                <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
                    <p className={"font-semibold"}>Archive Department</p>
                    <p>Please click to confirm archival of the department ({props.department.name})</p>
                    <BlueButton text={"Archive Department"} loading={archiveMutation.isLoading} onClick={handleArchiveToggle}/>
                </div>
            }
        </Modal>

        <Modal open={showUnassign} setOpen={setShowUnassign}>
            <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
                <p className={"font-semibold"}>Unassign Users From Department</p>
                <p>Please click to confirm unassigning all {props.department.userCount ?? 0} users from the department
                    ({props.department.name})</p>
                <BlueButton text={`Unassign ${props.department.userCount ?? 0} Users`} loading={unassignMutation.isLoading} onClick={handleUnassignUsers}/>
            </div>
        </Modal>

        <Modal open={showDelete} setOpen={setShowDelete}>
            <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
                <p className={"font-semibold"}>Permanently Delete Department</p>
                <p>Please click to confirm deleting the department {props.department.name} </p>
                <BlueButton text={"Delete Department"} loading={deleteMutation.isLoading} onClick={handleDelete}/>
            </div>
        </Modal>

        <div className={rowClasses}>
            <div className={"flex justify-center"}>
                <input type={"checkbox"}
                       checked={!!(props.department.id && ctx.selected.has(props.department.id))}
                       onChange={(e) => {
                           if (!props.department.id) {
                               return
                           }
                           if (e.target.checked) {
                               ctx.addSelected(props.department.id);
                           } else {
                               ctx.removeSelected(props.department.id);
                           }
                       }}
                />
            </div>
            <div className={"flex items-center"}>
                <Link to={`/management/departments/edit/${props.department.id}`}>
                    {props.department.name}
                </Link>
            </div>
            <div className={"flex items-center"}>
                <Link to={`/management/users?clientID=${props.department.clientID}&departmentIDs=${props.department.id}`}
                   target="_blank" rel="noreferrer">
                    {props.department.userCount ?? 0}
                </Link>
            </div>
            <div className={"flex items-center"}>
                <Link to={`/management/departments/${props.department.id}/guardians`}>
                    {props.department.guardianCount ?? 0}
                </Link>
            </div>
            <div className={""}>
                {archived &&
                    <ActionSelect label={"Select Action"}>
                        <Action label={"Unarchive"} onSelect={() => setShowArchive(true)}/>
                        {isSuperAdmin && <Action label={"Permanently Delete Department"} onSelect={() => setShowDelete(true)}/>}
                        <Action label={isCopied(props.department.id!) ? "Copied!" : "Copy ID"} onSelect={() => copyID(props.department.id!)} keepOpen/>
                    </ActionSelect>
                }

                {!archived &&
                    <ActionSelect label={"Select Action"}>
                        <Action label={"Edit"} onSelect={() => {
                            window.location.href = `/management/departments/edit/${props.department.id}`;
                        }}/>
                        {auth.hasAccessLevelClient && <Action label={"Manage Guardians"} onSelect={() => {
                            navigate(`/management/departments/${props.department.id}/guardians`)
                        }}/>}
                        <Action label={"Unassign All Users"} onSelect={() => setShowUnassign(true)}/>
                        <Action label={"Archive"} onSelect={() => setShowArchive(true)}/>
                        <Action label={isCopied(props.department.id!) ? "Copied!" : "Copy ID"} onSelect={() => copyID(props.department.id!)} keepOpen/>
                    </ActionSelect>
                }
            </div>
        </div>
    </>
}

const DepartmentTableHeader: React.FC = () => {
    const ctx = useDepartmentsManagement()

    return <div className="grid grid-cols-[5%_25%_25%_25%_20%]">
        <div className={"flex justify-center"}>
            <input type={"checkbox"}
                   onChange={(e) => {
                       if (e.target.checked) {
                           ctx.selectAll()
                       } else {
                           ctx.deselectAll()
                       }
                   }}
            />
        </div>
        <div>Name</div>
        <div>Users</div>
        <div>Guardians</div>
        <div>Actions</div>
    </div>
}

const MultiSelectActions: React.FC = () => {
    const ctx = useDepartmentsManagement()

    const count = useMemo(() => {
        return ctx.selected.size
    }, [ctx.selected])

    if (count == 0) {
        return null
    }

    return <div className="bg-white shadow-md p-4 mt-8">
        <div className="grid grid-cols-[15%_13%_24%_24%_24%]">
            <div className={"flex items-center"}>{count} Selected</div>
            <div></div>
            <div></div>
            <div className="w-64">
                <BlueButton text={`Archive (${count})`}/>
            </div>
            <div className="w-64">
                <BlueButton text={`Unassign Users (${count})`}/>
            </div>
        </div>
    </div>
}


export const DepartmentList: React.FC = () => {
    const ctx = useDepartmentsManagement()

    const [searchParams, setSearchParams] = useSearchParams()

    const [message, setMessage] = useState(searchParams.get("message"));

    useEffect(() => {
        if (message) {
            setTimeout(() => {
                setMessage(null)
                const newParams = new URLSearchParams(searchParams.toString());
                newParams.delete("message")
                setSearchParams(newParams);
            }, 5000)
        }
    }, [message]);


    return <Layout
        active={"departments"}
    >
        <div className="flex flex-col gap-8">
            {ctx.actionError && <div className="text-white bg-red rounded-md shadow-md p-4 flex flex-row gap-8 items-center">
                {ctx.actionError}
            </div>}

            {message && <div className="text-white bg-green shadow-md p-4 flex flex-row gap-8 items-center justify-center rounded-md">
                {message}
            </div>}

            <DepartmentFilterArchiveStatus/>
            <DepartmentFilterSearch/>
            <DepartmentTable/>
        </div>
        <MultiSelectActions/>
    </Layout>

}

DepartmentList.displayName = "DepartmentList";

export default DepartmentList;
